/**
 * Part del State necessari pel mapa de l'ecosistema 4.0 al Bages
 * Component: Mapa40State.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 26/04/2022
 * (c) Ajuntament de Manresa - abr 2022 - Departament de Sistemes d'Informació
 */

import { observable, action } from 'mobx';
import OlLayerImage from 'ol/layer/Image';
import OlImageSource from 'ol/source/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlTileSource from 'ol/source/Tile';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlOverlay from 'ol/Overlay';
import OlView from 'ol/View';
import OlMap from 'ol/Map';
import OlScaleLine from 'ol/control/ScaleLine';
import { Options } from 'ol/source/WMTS';
import { Extent, getCenter } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { IPropsMapa } from '../interfaces/IPropsMapa';
import Util from '../config/util';
import BaseState from './BaseState';
import { ILayer, ILayerGroup } from '../interfaces/ILayer';

// Zoom global mínim (lo més allunyat)
const minZ = 12;

// Zoom global màxim (lo més aprop)
const maxZ = 20;

export default class Mapa40State extends BaseState {
    // Constructor
    constructor(props: IPropsMapa) {
        super(props, 'mapa40');
    }

    public boxcheked: string[] = [];

    // Element HTML del contingut de l'overlay
    private popupContent: HTMLDivElement = document.createElement('div');

    // Element HTML de l'Overlay
    private popupElement: HTMLDivElement = this.createPopup(); // document.createElement('div');

    // El Overlay
    private popupOverlay = new OlOverlay({
        element: this.popupElement,
        autoPan: { animation: { duration: 250 } }
    });

    // Capa base inicial
    private iBase = '';

    // Posició inicial
    private iPosX = 0
    private iPosY = 0
    private iPosZoom = 0
    private iPosPtX = 0;
    private iPosPtY = 0;

    // array de filtres
    public filters: { feature: string, value: number }[] = [];

    // filtre per nom
    public nomFilter = '';

    // BaseLayer actual
    @observable actBaseLayer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;

    // Element del menu
    @observable anchorEl: HTMLButtonElement;

    // Torna el zoom inicial
    private getInitialZoom() : number {
        if (this.mapConfig.initialZoom) {
            return this.mapConfig.initialZoom;
        } else{
            return this.getMinZoom(null);
        }
    }

    // Torna el zoom mínim
    private getMinZoom(ly: ILayer) : number {
        let mz: number = minZ;
        if (this.mapConfig.minZoom) {
            mz = this.mapConfig.minZoom;
        }
        if (ly && ly.minZoom) {
            mz = Math.max(mz, ly.minZoom);
        }
        return mz;
    }

    // Torna el zoom màxim
    private getMaxZoom(ly: ILayer) : number {
        let mz: number = maxZ;
        if (this.mapConfig.maxZoom) {
            mz = this.mapConfig.maxZoom;
        }
        if (ly && ly.maxZoom) {
            mz = Math.min(mz, ly.maxZoom);
        }
        return mz;
    }

    // Crea el element del Popup
    private createPopup(): HTMLDivElement {
        const ppp: HTMLDivElement = document.createElement('div');
        ppp.className = 'guia-popup';
        ppp.id = 'popup';
        const pppclsr: HTMLAnchorElement = document.createElement('a');
        pppclsr.className = 'guia-popup-closer';
        pppclsr.id = 'popupcloser';
        pppclsr.href = '#';
        pppclsr.onclick = () => {
            this.popupOverlay.setPosition(undefined);
            document.getElementById('popupcloser').blur();
            return false;
        };
        ppp.appendChild(pppclsr);
        this.popupContent.id = 'popupcontent';
        this.popupContent.className = 'guia-popup-content';
        ppp.appendChild(this.popupContent);
        return ppp;
    }

    // Posa un QSelect
    public setqSelect(layer: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>, fid: string) {
        //console.log(fid);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const src: any = layer.getSource();
        if (fid) {
            if (src.params_.qfilter) {
                if (src.params_.qfilter === fid) {
                    return; // Ja teníem el mateix element seleccionat
                }
                delete src.params_.qfilter;
            }
            src.params_.qfilter = fid;
            src.refresh();

        } else {
            if (src.params_.qfilter) {
                delete src.params_.qfilter;
                src.refresh();
            }
        }
    }

    // Retorna l'objecte 'parms' parametres extres per crear un WMS layer
    public getLayerExtraParms2(ly: ILayer) {
        if (ly && ly.type === 'wms' && ly.qfilter) {
            let qfilters = "";
            const avaliableAttributes = [];
            this.filters.forEach(element => {
                if (!avaliableAttributes.includes(element.feature)) {
                    avaliableAttributes.push(element.feature)
                }
                //console.log(this.filters);
            });
            for (let i = 0; i < avaliableAttributes.length; i++) {
                const element = avaliableAttributes[i];
                const avaliableValues = []
                this.filters.forEach(e => {
                    if (element == e.feature) {
                        avaliableValues.push(e.value)
                    }
                });
                const values = avaliableValues.slice();
                qfilters += "[" + element + "] in (" + values.toString() + ")";
                // console.log(element);
                if (i < avaliableAttributes.length - 1) {
                    qfilters += " and "
                }
            }
            if (this.nomFilter) {
                if (qfilters) {
                    qfilters += " and "
                }
                qfilters += 'Match([NOM], "(?i).*' + Util.escapeRegExp(this.nomFilter) +  '.*")';
            }
            return { qfilter: qfilters };
        }
        return null;
    }

    // Afegeix una capa de referencia
    // Si retorn 0 és que la capa afegida està desactivada
    // Si retorna 1 és que esta activada però no es pot fer una identificació sobre els elements
    // Si retorna 2 és que està activada i es poden identificar els elements
    public addRefLayer(rl: ILayer | ILayerGroup): number {
        let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
        if (rl.type === 'wms') {
            const rll = rl as ILayer;
            olLy = this.createWMSLayer(rll.id, rll.layers, rll.url, this.getLayerExtraParms2(rll), rll.layers, rll.singleTile, true, true, rll.opacity, rll.info, this.getMinZoom(rll), this.getMaxZoom(rll));
            this.refLayers.push(olLy);
            this.actRefLayers.push(true);
            return 0;
        } else if (rl.type === 'wmts') {
            const rll = rl as ILayer;
            if (rll.cap) {
                olLy = this.createWMTSLayer(rll.id, rll.name, rll.cap as Options, true, rll.opacity, this.getMinZoom(rll), this.getMaxZoom(rll));
                this.refLayers.push(olLy);
                this.actRefLayers.push(true);
                return 0;
            }
            return 0;
        // } else if (rl.type === 'grp') { // Capes de referència dins d'un grup // En M40 no hi ha grups
        //     let ret = 0;
        //     (rl as ILayerGroup).layers.forEach((rl2) => { ret = this.addRefLayer(rl2) || ret; });
        //     return ret;
        }
        return 0;
    }

    // Processem els paràmetres de la URL
    private getURLParameters() {
        const usp: URLSearchParams = new URLSearchParams(window.location.search);
        // Mirem si ens han passat Base
        this.iBase = usp.get('Base');
    }

    // Posa el contingut del Popup d'informació d'un element
    private setPoupContent(c: Coordinate, content: string) {
        // content = content.replace(/\s+/g, ' ').trim(); // Traiem els retorns de carro, tabuladors, ...
        // Construim el títol
        const tit: string = Util.getBetweenI('<NOM>', '</NOM>', content); // Mirem si hi ha nom (versió nova de paràmetres)
        const logoSrc = Util.getBetweenI('<URLLOGO>', '</URLLOGO>', content);
        const txHtmlTit = '<b>' + "<img class=\"logoe\"src=" + logoSrc + "></img>" + (tit ? tit : 'Informació') + '</b><hr/>';
        // Construim el cos
        let txHtml = '';
        txHtml += "<strong>Descripcio: </strong>"
        txHtml += Util.getBetweenI('<DESCR>', '</DESCR>', content);
        txHtml += "<br>";
        txHtml += "<br>";
        txHtml += "<strong>Serveis: </strong>"
        txHtml += Util.getBetweenI('<SERVEIS>', '</SERVEIS>', content);
        txHtml += "<br>";
        txHtml += "<br>";
        txHtml += "<strong>Tecnologies: </strong> <br>"
        const tech = Util.getBetweenI('<TECNOLOGIES>', '</TECNOLOGIES>', content).split(",");
        console.log(tech);
        let techText = "";
        this.mapConfig.filters.forEach((fg) => {
            tech.forEach((t) => {
                if (fg.feature == "TIPTEC") {
                    fg.elements.forEach(element => {
                        if (t.includes(element.value.toString())) {
                            techText += " - " + element.name + "<br>"
                            
                        }
                    });

                }
            })
        })
        txHtml += techText;
        txHtml += "<br>";
        txHtml += "<br>Pagina Web: ";
        txHtml += "<a href=" + Util.getBetweenI('<URLWEB>', '</URLWEB>', content) + ">" + Util.getBetweenI('<URLWEB>', '</URLWEB>', content) + "</a>";
        txHtml += "<br>Telefon: ";
        txHtml += Util.getBetweenI('<TELEFON>', '</TELEFON>', content);
        txHtml += "<br>Adreça: ";
        txHtml += Util.getBetweenI('<ADRECA>', '</ADRECA>', content);

        this.popupContent.innerHTML = txHtmlTit + txHtml;
        this.popupOverlay.setPosition(c);
    }

    // Posa el zoom de la posició passada per paràmetre (zoom entre 14 i 20)
    private setInitialPos(posX: number, posY: number, posZoom: number, posPtX: number, posPtY: number) {
        const ext: Extent = [posX - 1, posY - 1, posX + 1, posY + 1];
        this.olview.fit(ext);
        if (posZoom >= 14 && posZoom <= 20) {
            this.olview.setZoom(posZoom);
        }
        if (posPtX && posPtY) {
            this.position = [posPtX, posPtY];
            this.setMarker();
        }
    }

    // Construim el mapa i hi afegim la llista de capes i el controls estàndard
    // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
    public createMap() {
        this.getURLParameters(); // llegim els paràmetres de la URL
        console.log(this.getURLParameters());
        let showNoms = false;
        this.mapConfig.baseLayers.forEach((bl) => { // Capes base
            const act: boolean = this.iBase && (bl.id.toLowerCase() === this.iBase.toLowerCase() || bl.layers.toLowerCase() === this.iBase.toLowerCase());
            let olLy: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource>;
            if (bl.type === 'wms') {
                olLy = this.createWMSLayer(bl.id, bl.layers, bl.url, this.getLayerExtraParms2(bl), bl.layers, bl.singleTile, true, act, 1.0, false, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la passada per paràmetres
                this.baseLayers.push(olLy);
            } else if (bl.type === 'wmts' && bl.cap) {
                olLy = this.createWMTSLayer(bl.id, bl.name, bl.cap as Options, act, 1.0, this.getMinZoom(bl), this.getMaxZoom(bl)); // Posem com a visible la 1a capa base
                this.baseLayers.push(olLy);
            }
            if (act) {
                showNoms = bl.noms;
                this.actBaseLayer = olLy;
            }
        });
        // NO hem trobat la capa base
        if (!this.actBaseLayer) {
            showNoms = this.mapConfig.baseLayers[0].noms;
            this.baseLayers[0].setVisible(true);
            this.actBaseLayer = this.baseLayers[0];
        }
        this.olview = new OlView({
            projection: this.olproj, // Projecció ETRS89 UTM 31N
            center: getCenter(this.mapConfig.initialExtent),
            extent: Util.isEmptyExtent(this.mapConfig.maxExtent) ? this.mapConfig.initialExtent : this.mapConfig.maxExtent,
            zoom: this.getInitialZoom(),
            minZoom: this.getMinZoom(null),
            maxZoom: this.getMaxZoom(null)
        });
        this.mapConfig.refLayers.forEach((rl) => { // Capes de referència (Només n'hi ha una)
            this.addRefLayer(rl);
            this.setRefLayerVisible(0, true, false);
        });

        // Tal com funciona ara el control MyLayerSwitcher, només afegim al mapa una capa base
        this.olmap = new OlMap({
            layers: [this.actBaseLayer].concat(this.refLayers), // this.baseLayers.concat(this.refLayers),
            view: this.olview
        });
        this.olmap.on('postcompose', this.olmap.updateSize);
        this.olmap.on('singleclick', this.processMapClick); // Funció per identificar un element
        if (this.mapConfig.scaleBar) { // Afegim el control escala si cal
            this.olmap.addControl(new OlScaleLine({
                units: 'metric',
                bar: false,
                steps: 4,
                text: true,
                minWidth: 140
            }));
        }
        const ctrls = this.olmap.getControls();
        // Sembla que el Duration fa que no funcioni
        ctrls.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });
        const intrs = this.olmap.getInteractions();
        intrs.forEach(element => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (element as any).duration_ = 0;
        });

        // Afegim la capa de noms
        if (this.mapConfig.nomsLayer) {
            if (this.mapConfig.nomsLayer.type === 'wms') {
                this.nomsLayer = this.createWMSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.url, this.getLayerExtraParms2(this.mapConfig.nomsLayer), this.mapConfig.nomsLayer.layers, true, true, showNoms, 1.0, false, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            } else if (this.mapConfig.nomsLayer.type === 'wmts' && this.mapConfig.nomsLayer.cap) {
                this.nomsLayer = this.createWMTSLayer(this.mapConfig.nomsLayer.id, this.mapConfig.nomsLayer.name, this.mapConfig.nomsLayer.cap as Options, showNoms, 1.0, this.getMinZoom(this.mapConfig.nomsLayer), this.getMaxZoom(this.mapConfig.nomsLayer));
                this.olmap.addLayer(this.nomsLayer);
            }
            this.setShowNoms(!!this.nomsLayer && showNoms);
            this.setNomsVisible(!!this.nomsLayer && showNoms);
        }

        // Afegim la capa de marcadors
        this.addMarkerLayer();

        // Creem el tooltip d'ajuda
        this.olmap.addOverlay(this.popupOverlay);

        // Mirem si volem l'event de quan el mapa s'ha carregat
        if (this.iPosX || this.iPosY) {
            this.olmap.once('rendercomplete', () => this.setInitialPos(this.iPosX, this.iPosY, this.iPosZoom, this.iPosPtX, this.iPosPtY));
        }
    }

    // Busquem el FID d'una finca (a partir de la referència cadastral) // No fa res
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedSetFIDFromRefCad(finRefCad1: string, finRefCad2: string) {
        return;
    }

    // Busquem el FID d'una finca (a partir del FinCod) // No fa res
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedSetFIDFromFinCod(finCod: number) {
        return;
    }

    // Busquem el FID d'un Numero (a partir del NumCod) // No fa res
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedSetFIDFromNumCod(numCod: number) {
        return;
    }

    // Busquem el FID d'una adreça i/o d'una finca (a partir d'una coordenada) // No fa res
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedSetFIDFromPos = (c: Coordinate) => {
        return;
    }

    // Llegim un element a partir d'una coordenada
    // iLy és el 1r layer on comencem a buscar. Aquí no s'utilitza
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedGetElementFromPos = (c: Coordinate, iLy?: number) => {
        if (this.actRefLayers.length > 0 && this.actRefLayers[0]) {
            const ly: OlLayerTile<OlTileSource> | OlLayerImage<OlImageSource> = this.refLayers[0];
            if (ly.get('info')) { // Mirem que podem demanar informació
                const src = (ly as OlLayerImage<OlImageSource>).getSource() as OlSourceImageWMS;
                if (src) {
                    const lyn = src.getParams();
                    const url = src.getFeatureInfoUrl(c, this.olview.getResolution(), src.getProjection(), { 'INFO_FORMAT': 'application/vnd.ogc.gml', 'QUERY_LAYERS': lyn.layers });
                    this.req.getRequest(url).then((result) => {
                        let content = '';
                        if (typeof result === 'string' || result instanceof String) {
                            content = Util.getBetween('<gml:featureMember>', '</gml:featureMember>', result as string);
                        }
                        if (content) { // Mostrar el diàleg
                            this.setPoupContent(c, content);
                        }
                    });
                }
            }
        }
    }

    // Busquem el FID d'un element element a partir d'una coordenada si el trobem mostrem la foto, si no mostrem la foto de la façana
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public derivedGetFIDFromPosPhotoElem = (c: Coordinate, iLy?: number): Promise<string | undefined> => {
        // En el Mapa40 aquesta funció no fa res
        return undefined;
    }

    // Posa o treu l'element del menú
    @action setAnchorEl(anchor: HTMLButtonElement) {
        this.anchorEl = anchor;
    }
}